import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    ChangeDetectorRef,
    ComponentFactoryResolver,
    ContentChild,
    Directive,
    Injector,
    Input,
    NgZone,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewRef
} from '@angular/core';
import { NgInject } from '@df/ng/ng-inject';
import { AppBusyStateService, IAppBusyStateTemplatePortal } from '@df/ui/display/busy-state/app-busy-state.service';

@Directive()
export abstract class AppBusyStateBroadcasterComponentDef implements AfterViewInit {
    readonly shouldDetectChanges: boolean = true;

    protected appBusyState?: IAppBusyStateTemplatePortal;

    protected appBusyStateDestroy?: () => void | undefined;

    /**
     * Pass template by component attribute `tpl`.
     *
     * @example
     * ```html
     * <app-busy [tpl]="customTpl">
     *   <ng-template></ng-template>
     * </app-busy>
     * ```
     */
    @ContentChild(TemplateRef) tplRef?: TemplateRef<any>;

    /**
     * Pass template by component attribute `tpl`.
     *
     * @example
     * ```html
     * <app-busy [tpl]="customTpl"></app-busy>
     * <ng-template #customTpl></ng-template>
     * ```
     */
    @Input() tpl?: TemplateRef<any>;

    /**
     * Time after which we close busy state
     */
    @Input() duration?: number;

    /**
     * Default component template.
     */
    @ViewChild('defaultTpl') defaultTpl?: TemplateRef<any>;

    @NgInject(AppBusyStateService) protected appBusyStateService!: AppBusyStateService;

    constructor(
        protected ngZone: NgZone,
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected injector: Injector,
        protected viewContainerRef: ViewContainerRef,
        protected _ref: ChangeDetectorRef
    ) {}

    ngAfterViewInit(): void {
        const appBusyState: IAppBusyStateTemplatePortal = {
            templatePortal: new TemplatePortal(this.tplRef || this.tpl || this.defaultTpl!, this.viewContainerRef)
        };

        if (this.duration !== undefined) {
            appBusyState.timeout = this.duration;
        }

        this.appBusyState = appBusyState;
    }

    protected show(): void {
        if (this.appBusyState) {
            this.appBusyStateDestroy = this.appBusyStateService.add(this.appBusyState);

            if (this.shouldDetectChanges) {
                this.detectChanges();
            }
        }
    }

    protected hide(): void {
        if (this.appBusyStateDestroy) {
            this.appBusyStateDestroy();
        }

        if (this.shouldDetectChanges) {
            this.detectChanges();
        }
    }

    detectChanges(): void {
        if (!(<ViewRef>this._ref).destroyed) {
            this._ref.detectChanges();
        }
    }
}
